import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { useAuth } from '../context/AuthContext';
import { useAuthGate } from '../context/AuthGateContext';
import {
  ArrowLeft, Star, MapPin, Share2, Heart, MessageCircle, Send,
  ChevronLeft, ChevronRight, CheckCircle, X, Wifi, Car,
  Dumbbell, Waves, Shield, Flame, Wind, BookOpen, Bike,
  UtensilsCrossed, Bath, Sofa, Info, Home, BedDouble, Clock
} from 'lucide-react';
import { mockData } from '../data/mockData';
import { ALL_AMENITIES, DURATION_OPTIONS, CITY_COORDS } from '../data/amenities';
import InspectionBooking from './InspectionBooking';

const API = process.env.REACT_APP_BACKEND_URL;

// Lazy-load the map to avoid SSR issues
const MapWithNoSSR = lazy(() => import('./MapView'));

// ── Amenity icon map ────────────────────────────────────────────────────────
const AMENITY_ICONS = {
  WiFi: Wifi, Internet: Wifi, Kitchen: UtensilsCrossed, Bathroom: Bath,
  'En-suite': Bath, 'Common Room': Sofa, AC: Wind, Heating: Flame,
  Laundry: Home, Dishwasher: UtensilsCrossed, Gym: Dumbbell,
  Pool: Waves, Parking: Car, Balcony: Home, Courtyard: Home,
  Garden: Home, Security: Shield, Fireplace: Flame,
  'Study Room': BookOpen, 'Bike Storage': Bike,
  'Couples OK': Heart, 'Pets Allowed': Home,
};

// ── Image Carousel + Lightbox ───────────────────────────────────────────────
const ImageCarousel = ({ images, name }) => {
  const [idx, setIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const imgs = images?.length ? images : ['https://images.pexels.com/photos/1545743/pexels-photo-1545743.jpeg?auto=compress&cs=tinysrgb&w=800'];
  const prev = (e) => { e?.stopPropagation(); setIdx(i => (i - 1 + imgs.length) % imgs.length); };
  const next = (e) => { e?.stopPropagation(); setIdx(i => (i + 1) % imgs.length); };

  // Keyboard navigation in lightbox
  useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape') setLightbox(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox, idx]);

  return (
    <>
      {/* Carousel thumbnail */}
      <div className="relative rounded-2xl overflow-hidden mb-6 group cursor-zoom-in"
        onClick={() => setLightbox(true)} title="Click to view full size">
        <img src={imgs[idx]} alt={name}
          className="w-full h-80 object-cover group-hover:brightness-90 transition-all duration-200"
          onError={e => { e.target.src = imgs[0]; }} />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" /></svg>
            Click to enlarge
          </div>
        </div>
        {imgs.length > 1 && (
          <>
            <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {imgs.map((_, i) => (
                <button key={i} onClick={e => { e.stopPropagation(); setIdx(i); }}
                  className={`w-2 h-2 rounded-full transition-all ${i === idx ? 'bg-white scale-125' : 'bg-white/50'}`} />
              ))}
            </div>
            <div className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
              {idx + 1} / {imgs.length}
            </div>
          </>
        )}
      </div>

      {/* Thumbnail strip for multiple images */}
      {imgs.length > 1 && (
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {imgs.map((src, i) => (
            <button key={i} onClick={() => { setIdx(i); setLightbox(true); }}
              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${i === idx ? 'border-[#094f6f] scale-105 shadow-md' : 'border-transparent opacity-60 hover:opacity-100'}`}>
              <img src={src} alt={`${name} ${i + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Fullscreen Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.95)' }}
          onClick={() => setLightbox(false)}>

          {/* Close button */}
          <button
            onClick={() => setLightbox(false)}
            className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 hover:bg-white/25 text-white rounded-full flex items-center justify-center transition-colors"
            aria-label="Close">
            <X className="w-6 h-6" />
          </button>

          {/* Counter */}
          {imgs.length > 1 && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/70 text-sm bg-black/40 px-3 py-1 rounded-full">
              {idx + 1} / {imgs.length}
            </div>
          )}

          {/* Prev arrow */}
          {imgs.length > 1 && (
            <button
              onClick={prev}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/10 hover:bg-white/25 text-white rounded-full flex items-center justify-center transition-colors"
              aria-label="Previous photo">
              <ChevronLeft className="w-7 h-7" />
            </button>
          )}

          {/* Full image */}
          <div className="relative max-w-5xl max-h-[90vh] w-full px-20 flex items-center justify-center"
            onClick={e => e.stopPropagation()}>
            <img
              src={imgs[idx]}
              alt={`${name} — photo ${idx + 1}`}
              className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl select-none"
              style={{ userSelect: 'none' }}
              draggable={false}
            />
          </div>

          {/* Next arrow */}
          {imgs.length > 1 && (
            <button
              onClick={next}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/10 hover:bg-white/25 text-white rounded-full flex items-center justify-center transition-colors"
              aria-label="Next photo">
              <ChevronRight className="w-7 h-7" />
            </button>
          )}

          {/* Dot indicators */}
          {imgs.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {imgs.map((_, i) => (
                <button key={i} onClick={e => { e.stopPropagation(); setIdx(i); }}
                  className={`w-2 h-2 rounded-full transition-all ${i === idx ? 'bg-white scale-125' : 'bg-white/40 hover:bg-white/70'}`} />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

// ── Rating bar ──────────────────────────────────────────────────────────────
const RatingBar = ({ rating, reviews }) => {
  if (!rating) return null;
  const dist = [40, 30, 15, 10, 5];
  return (
    <div className="flex gap-6 p-4 bg-gray-50 rounded-xl mb-6">
      <div className="text-center">
        <p className="text-4xl font-black text-gray-900">{rating}</p>
        <div className="flex gap-0.5 justify-center my-1">
          {[1,2,3,4,5].map(s => <Star key={s} className={`w-4 h-4 ${s <= Math.round(rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}`} />)}
        </div>
        <p className="text-xs text-gray-500">{reviews} reviews</p>
      </div>
      <div className="flex-1 space-y-1">
        {[5,4,3,2,1].map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-2">{s}</span>
            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
              <div className="h-1.5 rounded-full" style={{ width: `${dist[i]}%`, background: 'linear-gradient(90deg,#2cbdc4,#094f6f)' }} />
            </div>
            <span className="text-xs text-gray-400 w-7 text-right">{dist[i]}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Main component ──────────────────────────────────────────────────────────
const PropertyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showInspection, setShowInspection] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [msgText, setMsgText] = useState('');
  const [msgSent, setMsgSent] = useState(false);
  const [realProp, setRealProp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState(0); // index into DURATION_OPTIONS
  const [fav, setFav] = useState(false);
  const { user, token } = useAuth();
  const { requireAuth } = useAuthGate();

  // Only match against mock/demo data for small numeric IDs; real records use Mongo
  // ObjectIds, and parseInt() on those can collide with a demo entry's numeric id.
  const isMockId = /^\d{1,3}$/.test(id);
  const mockProp = isMockId ? mockData.featuredProperties.find(p => p.id === parseInt(id)) : null;

  useEffect(() => {
    if (!mockProp && id) {
      setLoading(true);
      fetch(`${API}/api/properties/${id}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => setRealProp(data))
        .catch(() => setRealProp(null))
        .finally(() => setLoading(false));
    }
  }, [id, mockProp]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-[#094f6f] border-t-transparent rounded-full animate-spin" /></div>;

  const property = mockProp
    ? {
        name: mockProp.name, location: mockProp.location, city: mockProp.location?.split(',')[0],
        price_per_week: parseFloat(mockProp.price?.replace(/[^0-9.]/g, '')),
        images: [mockProp.image], amenities: mockProp.amenities || [],
        roomTypes: mockProp.roomTypes, walkingDistance: mockProp.walkingDistance,
        description: mockProp.description || '', rating: mockProp.rating, reviews: mockProp.reviews,
        bedrooms: 1, bathrooms: 1, parking: 1, min_stay_weeks: 12,
      }
    : realProp
    ? {
        name: realProp.name, location: `${realProp.city}, Australia`, city: realProp.city,
        price_per_week: realProp.price_per_week, images: realProp.images || [],
        amenities: realProp.amenities || [],
        room_features: realProp.room_features || [],
        kitchen_features: realProp.kitchen_features || [],
        parking: realProp.parking || 'Not Available',
        pets: realProp.pets || 'Not Allowed',
        bills_included: realProp.bills_included || 'Not Included',
        furnishing: realProp.furnishing || 'Furnished',
        lease_term: realProp.lease_term || 'Flexible',
        roomTypes: [realProp.room_type],
        walkingDistance: realProp.university_nearby ? `Near ${realProp.university_nearby}` : realProp.city,
        description: realProp.description || '', rating: null, reviews: null,
        bedrooms: realProp.bedrooms || 1, bathrooms: realProp.bathrooms || 1, parking_count: 0,
        min_stay_weeks: 12, landlord_id: realProp.landlord_id,
        available_from: realProp.available_from || null,
        available_until: realProp.available_until || null,
        address: realProp.address || '',
      }
    : null;

  if (!property) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center"><h1 className="text-2xl font-bold text-gray-800 mb-4">Property Not Found</h1>
        <button onClick={() => window.history.back()} className="text-[#094f6f] underline">← Go Back</button>
      </div>
    </div>
  );

  const dur = DURATION_OPTIONS[selectedDuration];
  const weeklyDiscounted = property.price_per_week * (1 - dur.discount);
  const totalCost = weeklyDiscounted * dur.weeks;

  // If property has a fixed term (available_from + available_until), calculate actual duration
  const fixedWeeks = (() => {
    if (!property.available_from || !property.available_until) return null;
    const from = new Date(property.available_from);
    const until = new Date(property.available_until);
    const days = Math.round((until - from) / (1000 * 60 * 60 * 24));
    return Math.round(days / 7);
  })();
  const isFixedTerm = fixedWeeks !== null && fixedWeeks > 0;
  const displayWeeks = isFixedTerm ? fixedWeeks : dur.weeks;
  const displayTotal = isFixedTerm ? property.price_per_week * fixedWeeks : totalCost;
  const advanceRent = property.price_per_week * 2;
  const bond = property.price_per_week * 2;
  const moveInTotal = advanceRent + bond;
  const cityCoords = CITY_COORDS[property.city] || CITY_COORDS.default;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-16">
        {/* Header bar — non-sticky to avoid overlapping content */}
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
            <button onClick={() => window.history.back()} className="flex items-center text-[#094f6f] text-sm font-medium hover:opacity-70">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </button>
            <div className="flex gap-2">
              <button onClick={() => {
                const url = window.location.href;
                navigator.clipboard?.writeText(url);
              }} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 border border-gray-200 rounded-lg px-3 py-1.5">
                <Share2 className="w-3.5 h-3.5" /> Share
              </button>
              <button onClick={() => setFav(f => !f)} className={`flex items-center gap-1 text-xs border rounded-lg px-3 py-1.5 transition-colors ${fav ? 'bg-red-50 border-red-200 text-red-500' : 'border-gray-200 text-gray-500 hover:text-gray-800'}`}>
                <Heart className={`w-3.5 h-3.5 ${fav ? 'fill-red-500' : ''}`} /> Save
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* ── LEFT COLUMN ── */}
            <div className="lg:col-span-2 space-y-6">
              <ImageCarousel images={property.images} name={property.name} />

              {property.rating && <RatingBar rating={property.rating} reviews={property.reviews} />}

              {/* Title + basics */}
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{property.name}</h1>
                <div className="flex items-center gap-2 text-gray-500 mb-4">
                  <MapPin className="w-4 h-4" />
                  <span>{property.location}</span>
                  {property.walkingDistance && <span className="text-[#094f6f] text-sm font-medium">· {property.walkingDistance}</span>}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 py-5 border-y border-gray-100">
                  {[
                    { label: 'Bedrooms',   value: property.bedrooms,            Icon: BedDouble, color: '#094f6f' },
                    { label: 'Bathrooms',  value: property.bathrooms,           Icon: Bath,      color: '#094f6f' },
                    { label: 'Car Spaces', value: property.parking || 'None',   Icon: Car,       color: '#094f6f' },
                    { label: 'Min Stay',   value: `${property.min_stay_weeks} wks`, Icon: Clock, color: '#094f6f' },
                  ].map(item => (
                    <div key={item.label} className="flex flex-col items-center gap-1.5">
                      <div className="w-10 h-10 rounded-2xl flex items-center justify-center"
                        style={{ background: `${item.color}12`, border: `1.5px solid ${item.color}20` }}>
                        <item.Icon className="w-5 h-5" style={{ color: item.color }} />
                      </div>
                      <p className="text-xl font-black text-[#094f6f]">{item.value}</p>
                      <p className="text-xs text-gray-400 font-medium">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Room types */}
              {property.roomTypes?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {property.roomTypes.map((t, i) => (
                    <span key={i} className="px-3 py-1 rounded-full text-xs font-medium bg-sky-50 text-[#094f6f] border border-sky-100">{t}</span>
                  ))}
                </div>
              )}

              {/* Description */}
              {property.description && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">About This Property</h3>
                  <p className="text-gray-600 leading-relaxed" style={{ whiteSpace: 'pre-wrap' }}>{property.description}</p>
                </div>
              )}

              {/* Amenities */}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Amenities</h3>
                {property.amenities.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {property.amenities.map((a, i) => {
                      const amenityData = ALL_AMENITIES.find(am => am.id === a) || { label: a, emoji: '✓' };
                      const Icon = AMENITY_ICONS[a];
                      return (
                        <div key={i} className="flex items-center gap-2.5 p-3 bg-gray-50 rounded-xl border border-gray-100 hover:border-sky-200 transition-colors">
                          {Icon ? <Icon className="w-4 h-4 text-[#2cbdc4] flex-shrink-0" /> : <span className="text-sm">{amenityData.emoji}</span>}
                          <span className="text-sm text-gray-700 font-medium">{amenityData.label}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">No amenities listed.</p>
                )}
              </div>

              {/* Room Features */}
              {property.room_features?.length > 0 && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Room Features</h3>
                  <div className="flex flex-wrap gap-2">
                    {property.room_features.map((f, i) => (
                      <span key={i} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-full text-sm text-gray-700">
                        <span className="text-[#2cbdc4]">✓</span>{f}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Kitchen Features */}
              {property.kitchen_features?.length > 0 && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Kitchen Features</h3>
                  <div className="flex flex-wrap gap-2">
                    {property.kitchen_features.map((f, i) => (
                      <span key={i} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-full text-sm text-gray-700">
                        <span className="text-[#2cbdc4]">✓</span>{f}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Property Details — Parking, Pets, Bills, Furnishing */}
              {(property.parking || property.pets || property.bills_included || property.furnishing) && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Property Details</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { label: 'Parking',       value: property.parking,       icon: '🚗' },
                      { label: 'Pets Policy',   value: property.pets,          icon: '🐾' },
                      { label: 'Bills',         value: property.bills_included, icon: '💡' },
                      { label: 'Furnishing',    value: property.furnishing,    icon: '🛋️' },
                      { label: 'Minimum Lease', value: property.lease_term,    icon: '📅' },
                    ].filter(d => d.value && d.value !== 'Not Available' && d.value !== 'Not Included' && d.value !== 'Not Allowed').map(d => (
                      <div key={d.label} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                        <span className="text-lg">{d.icon}</span>
                        <div>
                          <p className="text-xs text-gray-400 font-medium">{d.label}</p>
                          <p className="text-sm text-gray-800 font-semibold">{d.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Map */}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Location</h3>
                <div className="rounded-2xl overflow-hidden" style={{ height: '400px' }} data-testid="property-location-map">
                  <Suspense fallback={<div className="w-full h-full bg-gray-100 rounded-2xl animate-pulse" />}>
                    <MapWithNoSSR
                      properties={[{ ...property, id: id, price: `A$${property.price_per_week}` }]}
                      onPropertyClick={() => {}}
                    />
                  </Suspense>
                </div>
                <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                  <Info className="w-3 h-3" /> Exact address provided after booking confirmation
                </p>
              </div>
            </div>

            {/* ── RIGHT SIDEBAR ── */}
            <div className="lg:col-span-1">
              <div className="sticky top-8 space-y-4">
                <Card className="border border-sky-100 shadow-xl">
                  <CardContent className="p-5">
                    {/* Price + duration selector or fixed term */}
                    <div className="mb-4">
                      <div className="flex items-baseline gap-1 mb-1">
                        <span className="text-3xl font-black" style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                          A${isFixedTerm ? property.price_per_week : Math.round(weeklyDiscounted)}
                        </span>
                        <span className="text-gray-500 text-sm">/week</span>
                        {!isFixedTerm && dur.discount > 0 && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium ml-1">{(dur.discount * 100).toFixed(0)}% off</span>}
                      </div>
                      <p className="text-xs text-gray-400 flex items-center gap-1"><Info className="w-3 h-3" />
                        {isFixedTerm
                          ? `Fixed term: ${fixedWeeks} weeks (${property.available_from} to ${property.available_until})`
                          : 'Minimum stay: 3 months / 12 weeks'
                        }
                      </p>
                    </div>

                    {/* Duration selector — only for ongoing/flexible listings */}
                    {!isFixedTerm && (
                      <div className="mb-4">
                        <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Select Duration</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                          {DURATION_OPTIONS.map((opt, i) => (
                            <button key={i} onClick={() => setSelectedDuration(i)}
                              className={`py-2 px-3 rounded-xl text-xs font-medium border transition-all text-center ${
                                selectedDuration === i ? 'text-white border-transparent shadow-sm' : 'border-gray-200 text-gray-600 hover:border-[#094f6f]'
                              }`}
                              style={selectedDuration === i ? { background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' } : {}}>
                              <div className="font-bold">{opt.label}</div>
                              <div className="opacity-80">{opt.subLabel}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Fixed-term badge */}
                    {isFixedTerm && (
                      <div className="mb-4 p-2.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 flex items-center gap-2">
                        <span>📅</span>
                        <span><strong>Short-term listing:</strong> Available for {fixedWeeks} weeks only</span>
                      </div>
                    )}

                    {/* Total cost */}
                    <div className="mt-3 p-3 bg-sky-50 rounded-xl border border-sky-100 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{displayWeeks} {isFixedTerm ? 'weeks total' : 'week total'}</span>
                        <span className="font-bold text-[#094f6f]">A${Math.round(displayTotal).toLocaleString()}</span>
                      </div>
                      {!isFixedTerm && dur.discount > 0 && (
                        <div className="flex justify-between text-xs text-green-600 mt-1">
                          <span>You save</span>
                          <span className="font-semibold">A${Math.round(property.price_per_week * dur.weeks - totalCost).toLocaleString()}</span>
                        </div>
                      )}
                    </div>

                    {/* Move-in cost */}
                    <div className="mb-4 p-3 bg-gray-50 rounded-xl border border-gray-100 text-sm space-y-1.5">
                      <p className="font-semibold text-gray-800 mb-2">Move-in Cost Breakdown</p>
                      <div className="flex justify-between text-gray-600">
                        <span>2 Weeks Advance Rent</span>
                        <span className="font-medium">A${Math.round(advanceRent)}</span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>2 Weeks Bond</span>
                        <span className="font-medium">A${Math.round(bond)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-gray-900 pt-2 border-t border-gray-200">
                        <span>Total Move-in Cost</span>
                        <span className="text-[#094f6f]">A${Math.round(moveInTotal)}</span>
                      </div>
                    </div>

                    {/* No-fee banner */}
                    <div className="text-center text-xs text-green-700 bg-green-50 rounded-lg py-2 mb-4 font-medium">
                      No booking fees · Free cancellation up to 24hrs
                    </div>

                    {/* Action buttons */}
                    <div className="space-y-2">
                      <button
                        onClick={() => {
                          if (!user) { requireAuth(`/property/${id}`); return; }
                          setShowInspection(true);
                        }}
                        className="w-full py-3.5 rounded-xl font-bold text-white text-sm hover:opacity-90 transition-opacity"
                        style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
                        Book Inspection
                      </button>
                      <button
                        onClick={() => {
                          if (!user) { requireAuth(`/property/${id}`); return; }
                          setShowMessage(true);
                        }}
                        className="w-full py-3 rounded-xl font-semibold text-[#094f6f] text-sm border-2 border-[#094f6f] hover:bg-sky-50 transition-colors flex items-center justify-center gap-2">
                        <MessageCircle className="w-4 h-4" /> Message
                      </button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* Inspection Booking Modal */}
      {showInspection && <InspectionBooking property={property} onClose={() => setShowInspection(false)} />}

      {/* Message Modal */}
      {showMessage && (
        <div className="fixed inset-0 z-[9000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setShowMessage(false); setMsgSent(false); setMsgText(''); }} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
              <div className="flex items-center gap-2 text-white"><MessageCircle className="w-5 h-5" /><p className="font-bold">Message Landlord</p></div>
              <button onClick={() => { setShowMessage(false); setMsgSent(false); setMsgText(''); }} className="text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              {msgSent ? (
                <div className="text-center py-6">
                  <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-3" />
                  <h3 className="text-xl font-bold mb-2">Message Sent!</h3>
                  <p className="text-gray-500 text-sm">The landlord has been notified.</p>
                  <button onClick={() => navigate('/student-dashboard', { state: { tab: 'messages' } })}
                    className="mt-4 text-white px-6 py-2.5 rounded-xl text-sm font-semibold"
                    style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>Go to Messages</button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-4">Send a message about <strong>{property.name}</strong></p>
                  <textarea value={msgText} onChange={e => setMsgText(e.target.value)}
                    placeholder={`Hi, I'm interested in ${property.name}. Is it still available?`}
                    rows={4} className="w-full px-3 py-2.5 border border-sky-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30 resize-none mb-4" />
                  <button onClick={async () => {
                    if (!msgText.trim()) return;
                    if (realProp?.landlord_id && token) {
                      try {
                        await fetch(`${API}/api/messages/send`, {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ recipient_id: realProp.landlord_id, content: msgText, property_id: id }),
                        });
                      } catch {}
                    }
                    setMsgSent(true);
                  }} disabled={!msgText.trim()}
                    className="w-full py-3 rounded-xl font-semibold text-white disabled:opacity-40 flex items-center justify-center gap-2"
                    style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
                    <Send className="w-4 h-4" /> Send Message
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyDetails;
